/**
 * «Нуль не зникає» (Danyil 24.10.2026: «дуже багато жаліються — у полі стоїть
 * 0, і коли хочуть ввести, той нуль не зникає»).
 *
 * Перша лінія — у джерелі: NumInput показує нуль підказкою (`blankZero` за
 * замовчуванням), нові рядки форм — порожні з placeholder «0». Тут — страховка
 * на ВЕСЬ сайт для решти: числове поле, де стоїть голий нуль («0», «0.00»,
 * «0,0»), при фокусі виділяється — перша цифра замінює нуль, а не дописується
 * до нього («05», «0,5»). Одне місце замість латок на кожному екрані.
 */
const BARE_ZERO = /^\s*0+([.,]0*)?\s*$/;

export function isNumericField(el: EventTarget | null): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false;
  return el.type === "number" || el.inputMode === "decimal" || el.inputMode === "numeric";
}

export function isBareZero(v: string): boolean {
  return BARE_ZERO.test(v);
}

export function installZeroSelect(doc: Document = document): void {
  doc.addEventListener("focusin", (e) => {
    const el = e.target;
    if (!isNumericField(el) || !isBareZero(el.value)) return;
    // Після фокуса: на телефоні виділення, поставлене в самому focus, браузер
    // скидає власним розміщенням курсора.
    requestAnimationFrame(() => {
      try { el.select(); } catch { /* поле зникло — нічого виділяти */ }
    });
  });
}

/**
 * Число з сервера → значення поля для РЕДАГУВАННЯ: нуль стає порожнім полем
 * (у полі видно підказку «0»), решта — як є. Збереження порожнього = 0 —
 * це робить форма (`|| "0"`, `saveNum`). Danyil 02.11.2026: «всюди нулі — треба
 * витерти й потім вписувати; має бути: на фоні нуль, а не вписав — буде нуль».
 */
export function editNum(v: string | number | null | undefined): string {
  if (v == null) return "";
  const s = String(v);
  return isBareZero(s) ? "" : s;
}

/** Порожнє поле на збереженні — нуль (пара до `editNum`). Кома — крапкою. */
export function saveNum(v: string | null | undefined): string {
  return (v ?? "").trim() === "" ? "0" : decimalText(String(v).trim());
}

/**
 * «0,5» і «0.5» — одне число (Danyil 03.11.2026: «уніфікуй це в одне»).
 *
 * У дробовому полі кома одразу стає крапкою — ще ДО того, як значення побачить
 * форма. Тож далі по ланцюгу (підрахунки на екрані, запит на сервер, бек на
 * Pydantic) завжди йде одна форма запису, і «0,5» не повертається 422-кою,
 * а «1,250 м» не читається на екрані як тисяча двісті.
 */
export function decimalText(v: string): string {
  return v.replace(/,/g, ".");
}

/** Чи це текстове дробове поле (`inputMode="decimal"`). `type="number"` сюди
 *  не входить: кому браузер у ньому просто губить, тож дробові поля — текстові. */
export function isDecimalField(el: EventTarget | null): el is HTMLInputElement {
  return el instanceof HTMLInputElement && el.type === "text" && el.inputMode === "decimal";
}

export function installDecimalComma(doc: Document = document): void {
  // Сеттер ПРОТОТИПУ, а не `el.value = …`: React стежить за значенням через
  // власний сеттер екземпляра. Пишемо в обхід — і React бачить зміну й віддає
  // формі вже «0.5» у тому ж onChange. Перехоплення — у capture до React.
  const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  doc.addEventListener("input", (e) => {
    const el = e.target;
    if (!setValue || !isDecimalField(el) || !el.value.includes(",")) return;
    const pos = el.selectionStart;
    setValue.call(el, decimalText(el.value));
    if (pos !== null) el.setSelectionRange(pos, pos);
  }, true);
}
